// -- Product Showcase ----------------------------------------
// A poster frame with a circular play button; clicking it swaps to the
// native <video> (which then carries its own controls). No custom player
// chrome - the browser draws everything once the video is visible.
// State API per AGENTS.md: 'default' (poster) | 'playing' (video).

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals, defussQuery, componentState, bindComponent } from '../../../shared/state-api.js';

const df$ = defussGlobals();
const dfDollar = defussQuery();

const productShowcaseStates = ['default', 'playing'];

// VERIFIED: (verify's API docs gate) the states below are exactly the declared ones, each
// described, and every config field typed, described and named in the code.
/** setState() configs per state - the showcase's states take none. */
export interface ProductShowcaseStateConfigs {
  /** The poster and the play button; the video hidden and unloaded. */
  default: {};
  /** The video visible and playing (muted); poster and button hidden. */
  playing: {};
}

/**
 * The markup of a state, for render(): the attributes a state writes, applied
 * to a detached copy of the authored markup ('default' IS the authored
 * markup). The live element gets the same markup from triggerStateChange -
 * the e2e render round trip proves they agree.
 */
function applyMarkup(el, stateName) {
  dfDollar(el).attr('data-state', stateName === 'playing' ? 'playing' : 'default');
}

/**
 * UI side of setState: the only function touching the DOM for a state
 * change. 'playing' hides the poster/play button (CSS, via data-state) and
 * starts playback; 'default' pauses and rewinds so the poster returns.
 */
function triggerStateChange(showcase, stateName, _config) {
  const video = dfDollar(showcase).find('video').get(0);
  switch (stateName) {
    case 'default':
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      showcase.dataset.state = 'default';
      break;
    case 'playing':
      showcase.dataset.state = 'playing';
      // muted: playback must never be blocked (or blast audio) when a
      // screenshot/agent drives setState without a user gesture
      if (video) {
        video.muted = true;
        video.play().catch(() => {
          /* autoplay blocked - poster stays, controls still work */
        });
      }
      break;
  }
}

/** Registry-level API; pass the showcase element explicitly. Unknown names throw. */
export const productShowcaseApi = componentState({
  component: 'product-showcase',
  states: productShowcaseStates,
  apply: (showcase, state) => triggerStateChange(showcase, state.name, state.config),
  read: (showcase, state) => {
    // reflect reality: a user pausing the native controls returns to the poster
    const playing = showcase.dataset.state === 'playing';
    return {
      name: showcase.dataset.stateName || (playing ? 'playing' : 'default'),
      config: state.config,
    };
  },
  markup: (el, state) => applyMarkup(el, state.name),
});

df$.productShowcaseApi = productShowcaseApi;
df$.productShowcaseStates = productShowcaseStates;

function init() {
  dfDollar('.mk-showcase:not([data-init])')
    .toArray()
    .forEach((showcase) => {
      showcase.dataset.init = '';
      showcase.dataset.state = 'default';
      // el.store + el.api (AGENTS.md "State through stores")
      bindComponent(showcase, productShowcaseApi);
      dfDollar(showcase)
        .find('.mk-showcase-play')
        .get(0)
        ?.addEventListener('click', () => {
          productShowcaseApi.setState(showcase, 'playing');
        });
      // native pause/ended returns to the poster: route it through the API so
      // the visible state and getState() never diverge (pause covers `ended` too)
      dfDollar(showcase)
        .find('video')
        .get(0)
        ?.addEventListener('pause', () => {
          if (showcase.dataset.state === 'playing') productShowcaseApi.setState(showcase, 'default');
        });
    });
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });
